import React from 'react';
import { Form, Formik, FormikHelpers, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import * as Yup from 'yup';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import * as authService from '../../api/authService';
import useAuthContext from '../../hooks/useAuthContext';
import useNavContext from '../../hooks/useNavContext';
import useNoSleep from '../../hooks/useNoSleep';
import Flexlogo from '../../assets/images/logo-red.png';
import { version } from '../../../package.json';

const useStyles = makeStyles((theme) => ({
  content: {
    [theme.breakpoints.up('sm')]: {
      width: 400,
      margin: '0 auto',
      marginTop: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      margin: '0 auto',
      marginTop: theme.spacing(5),
    },
  },
  form: {
    marginTop: theme.spacing(4),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(),
    justifyContent: 'center',
  },
  forgetContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  logoContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 140,
    height: 50,
  },
  noAccountContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  companion: {
    marginTop: 40,
    fontWeight: theme.typography.fontWeightLight,
    fontSize: 28,
  },
  subtext: {
    marginTop: 30,
    fontWeight: theme.typography.fontWeightLight,
    fontSize: 16,
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  versionText: {
    fontWeight: theme.typography.fontWeightLight,
    color: theme.palette.grey[500],
    fontSize: 14,
    textAlign: 'center',
    position: 'fixed',
    bottom: 24,
    left: 0,
    width: '100%',
  },
}));

interface Values {
  emailUserName: string;
  password: string;
}

const ValidationSchema = Yup.object().shape({
  emailUserName: Yup.string().required('Required'),
  password: Yup.string()
    .required('Required')
    .min(8, 'Must be greater than 8 characters'),
});

const initialValues: Values = {
  emailUserName: '',
  password: '',
};

/**
 * SignIn displays the sign in page.
 */
export default function SignIn() {
  const classes = useStyles();
  const auth = useAuthContext();
  const nav = useNavContext();
  useNoSleep(false);

  const handleSubmit = async (
    values: Values,
    { setFieldError, setSubmitting }: FormikHelpers<Values>,
  ) => {
    const { emailUserName, password } = values;

    try {
      const res = await authService.signIn(
        emailUserName.toLowerCase(),
        password,
      );

      auth?.setSession({
        id: res.id,
        token: res.token,
        userId: '1',
      });
      nav?.setNav({
        menuNav: 'home',
      });
    } catch (e: any) {
      e.data.forEach((err: any) => {
        setFieldError(err.meta.attribute, err.detail);
      });

      setSubmitting(false);
    }
  };

  return (
    <div>
      <Grid container>
        <Grid item xs={12}>
          <div className={classes.content}>
            <div className={classes.logoContainer}>
              <img src={Flexlogo} className={classes.logo} alt="flex reward" />
              <Typography className={classes.companion}>
                COMPANION APP
              </Typography>
              <Typography className={classes.subtext}>
                Signup customers, add stamps and redeem rewards
              </Typography>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={ValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form noValidate className={classes.form}>
                  <Field
                    component={TextField}
                    className={classes.textField}
                    name="emailUserName"
                    autoFocus
                    fullWidth
                    variant="standard"
                    label="Username or Email"
                    inputProps={{ style: { textTransform: 'lowercase' } }}
                  />

                  <Field
                    component={TextField}
                    className={classes.textField}
                    name="password"
                    type="password"
                    fullWidth
                    variant="standard"
                    label="Password"
                  />

                  <div className={classes.actions}>
                    <Button
                      type="submit"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      LOGIN
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
            <div className={classes.footerContainer}>
              <Typography
                className={classes.versionText}
                onClick={() => window.location.reload()}
              >
                Version {version}
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
}
